// Importing React hooks and other utilities from the library and context files.
import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  fixRelativePath,
  getQueryStringParam,
} from "../../utils/bullseyeUtils";
import BullseyeLoader from "../bullseyeLoader/bullseyeLoader";
import { LocatorConfigurationContext } from "../../context/locatorConfiguration";
import { setSecurityParameters } from "../../services/httpService";
import { getSearchLocation } from "../../services/restSearchService";
import Coupon from "./coupon";

// Defining TypeScript interface for route parameters.
interface RouteParams {
  interfaceName: string;
}

interface CouponData {
  ClientId: number;
  CouponId: number;
  CouponImage: string | null;
  CouponText: string | null;
  EndDate: string | null;
  Name: string | null;
  PromoPageURL: string | null;
  PromotionName: string | null;
  StartDate: string | null;
  ThumbnailImage: string | null;
}

interface Location {
  Id: number;
  Name?: string;
  Address1?: string;
  City?: string;
  StateAbbr?: string;
  PostCode?: string;
  PhoneNumber?: string;
  Coupons: CouponData[];
}

// Main functional component for handling coupons.
const CouponContainer: React.FC = () => {
  // State for managing the loading and data of coupons.
  const [coupon, setCoupon] = useState<{
    loaded: boolean;
    location: Location; // Specify a more specific type if possible.
  }>({
    loaded: false,
    location: {
      // Providing an initial structure matching the Location interface.
      Coupons: [], // Ensure this is initialized as an empty array.
      Name: "",
      Address1: "",
      City: "",
      StateAbbr: "",
      PostCode: "",
      PhoneNumber: "",
      Id: 0,
    },
  });

  // Using useContext to access locator configuration context.
  const {
    setLocatorConfiguration,
    locatorConfigurationIsLoading,
    interfaceConfiguration,
    // interfaceInternalConfiguration and interfaceNotFound appear to be unused.
  } = useContext(LocatorConfigurationContext);

  // Extracting interfaceName from URL parameters.
  const { interfaceName } = useParams<RouteParams>();

  // Query string handling for various parameters.
  const queryString = window.location.search;
  let languageCode = getQueryStringParam(queryString, "langcode");
  const qsLocationIds = getQueryStringParam(queryString, "locationId");
  // qsCouponType appears unused; remove or use as needed.
  const qsCouponType = getQueryStringParam(queryString, "couponType");

  // Determining the browser's language setting.
  const browserLanguage = window.navigator.language;
  const indexBrowserLanguage = browserLanguage.indexOf("-");
  const languageBrowserCode =
    indexBrowserLanguage === -1
      ? browserLanguage
      : browserLanguage.substring(0, indexBrowserLanguage);

  // Setting languageCode if not provided in the query string.
  if (!languageCode) {
    languageCode = languageBrowserCode;
  }

  // Configuration setup effect, run on component mount.
  useEffect(() => {
    setLocatorConfiguration({
      interfaceName,
      apiKeyName: "",
      language: "",
      interfaceId: 0,
    });
  }, [interfaceName]);

  // Security parameters are set once the locator configuration is loaded.
  useEffect(() => {
    if (
      !locatorConfigurationIsLoading &&
      interfaceConfiguration &&
      interfaceConfiguration.clientId > 0 &&
      interfaceConfiguration.apiKey
    ) {
      setSecurityParameters(
        interfaceConfiguration.clientId,
        interfaceConfiguration.apiKey
      );
    }
  }, [locatorConfigurationIsLoading, interfaceConfiguration]);

  // Fetching location data and updating coupon state.
  useEffect(() => {
    if (locatorConfigurationIsLoading || !qsLocationIds) return;

    const getLocationAsync = async () => {
      const location = await getSearchLocation(qsLocationIds, null);

      location.Coupons = location.Coupons.map(
        (coupon: { CouponImage: string; ThumbnailImage: string }) => {
          coupon.CouponImage = fixRelativePath(coupon.CouponImage);
          coupon.ThumbnailImage = fixRelativePath(coupon.ThumbnailImage);
          return coupon;
        }
      );

      setCoupon({ loaded: true, location: location });
    };

    getLocationAsync();
  }, [locatorConfigurationIsLoading, qsLocationIds]);

  // Conditional rendering based on loading state.
  return (
    <div>
      {coupon.loaded ? (
        coupon.location.Coupons.map((c) => (
          <Coupon
            key={c.CouponId}
            couponName={c.Name}
            couponText={c.CouponText}
            couponImage={c.CouponImage}
            locationName={coupon.location.Name}
            address1={coupon.location.Address1}
            city={coupon.location.City}
            stateAbbr={coupon.location.StateAbbr}
            postCode={coupon.location.PostCode}
            phone={coupon.location.PhoneNumber}
          />
        ))
      ) : (
        <BullseyeLoader />
      )}
    </div>
  );
};

// Exporting the component for use elsewhere in the application.
export default CouponContainer;
